import classNames from 'classnames';
// same composite the rescue PDP leads with (`product_images/mini/rescue-hero.jpg` in ecommerce),
// so the shop handoff is visually continuous
import miniInHandImage from '../../../../assets/images/mini-in-hand.jpg';
import { Links } from '../../../../lib/constants';
import { petPronouns, speciesPlural } from '../../../../lib/petUtils';
import { rescueInitials } from '../../../../lib/rescueUtils';
import { gqlTypes } from '../../../../types/gqlTypes';
import { useInViewOnce } from '../../../../lib/useInViewOnce';
import { useIsInView } from '../../../../lib/useIsInView';
import styles from './MergedSuccess.module.scss';

/**
 * Measured basis for this claim: 678 rescue households own a Fi collar, across roughly 430
 * distinct rescue orgs. The prototype's copy says "over 700 rescues", which is not supportable
 * on either reading, so this states the number we can defend. One constant, so copy can swap
 * the wording without hunting through the component.
 */
const RESCUE_PROOF_COUNT = 'Over 400 rescues';

export interface RescueOffer {
  /** free text authored per GWA program; never hardcode a duration or amount */
  offer: string;
  orgName: string | null;
}

export interface MergedSuccessProps {
  petName: string;
  chipId: string;
  gender?: gqlTypes.Gender | null;
  species?: gqlTypes.Species | null;
  profilePhotoUrl?: string | null;
  /** a transfer request was sent; the chip is NOT active yet, so the copy must not claim it is */
  isTransfer: boolean;
  /** present only when the selling rescue has an active GWA program */
  rescueOffer?: RescueOffer | null;
  shopUrl: string;
  /**
   * The shop handoff opens a new tab so `onCtaClick` can move this one to the chip list. The in-site
   * bag is the same flow, so it navigates in place instead.
   */
  ctaOpensNewTab?: boolean;
  onCtaClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onSecondaryClick?: () => void;
  onOfferInView: () => void;
}

// marks lifted from the prototype so the shapes and fills match it exactly
function YesMark() {
  return (
    <svg width="13" height="13" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M17 5.4 15.6 4 7.9 12.7 3.4 8.5 2 9.9l6 5.6L17 5.4Z" fill="#2CB355" />
    </svg>
  );
}

function NopeMark() {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M15.8 5.6 14.4 4.2 10 8.6 5.6 4.2 4.2 5.6 8.6 10l-4.4 4.4 1.4 1.4L10 11.4l4.4 4.4 1.4-1.4L11.4 10l4.4-4.4Z"
        fill="#FF004F"
      />
    </svg>
  );
}

function Capability({ children, can }: { children: React.ReactNode; can: boolean }) {
  return (
    <div className={classNames(styles.capability, { [styles.cannot]: !can })}>
      <span className={classNames(styles.capabilityIcon, can ? styles.yes : styles.nope)}>
        {can ? <YesMark /> : <NopeMark />}
      </span>
      {children}
    </div>
  );
}

export default function MergedSuccess({
  petName,
  chipId,
  gender,
  species,
  profilePhotoUrl,
  isTransfer,
  rescueOffer,
  shopUrl,
  ctaOpensNewTab = true,
  onCtaClick,
  onSecondaryClick,
  onOfferInView,
}: MergedSuccessProps) {
  const { subject, possessive, object, usesPluralVerb } = petPronouns(gender);
  const offerBlockRef = useInViewOnce<HTMLDivElement>(onOfferInView);
  // reveals the secondary "go to my chips" CTA once the user scrolls past the offer content
  const [hasReachedEnd, endOfContentRef] = useIsInView<HTMLDivElement>();
  const rescueName = rescueOffer ? (rescueOffer.orgName ?? 'Your rescue') : null;

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.badge}>{isTransfer ? 'Transfer requested' : 'Registered'}</div>
        <div className={styles.title}>
          {isTransfer ? `${petName}'s transfer request is on its way.` : `${petName}'s chip is active.`}
        </div>

        <div className={classNames(styles.card, styles.statusCard)}>
          <div className={styles.avatar}>
            {profilePhotoUrl ? <img src={profilePhotoUrl} alt={petName} /> : <span>{petName.charAt(0)}</span>}
          </div>
          <div className={styles.statusInfo}>
            <div className={classNames(styles.statusPill, { [styles.pending]: isTransfer })}>
              {isTransfer ? 'Transfer pending' : 'Active'}
            </div>
            <div className={styles.statusName}>{petName}</div>
            <div className={styles.statusChip}>Chip {chipId}</div>
          </div>
        </div>

        <div className={classNames(styles.card, styles.offerCard)} ref={offerBlockRef}>
          <div className={styles.capabilityTitle}>What {possessive} microchip can and can’t do</div>
          <div className={styles.capabilityList}>
            <Capability can={true}>Let a vet or shelter identify {object}</Capability>
            <Capability can={true}>Give them your phone number</Capability>
            {/* subject pronoun, and the verb agrees: "she escapes" but "they escape" */}
            <Capability can={false}>
              Alert you when {subject} {usesPluralVerb ? 'escape' : 'escapes'}
            </Capability>
            <Capability can={false}>Track {object} live on a map</Capability>
          </div>

          <div className={styles.offerFoot}>
            <div className={styles.pivot}>A Fi GPS tracker does the other two.</div>

            {rescueOffer && rescueName ? (
              <div className={styles.rescueRow}>
                <div className={styles.rescueBadge} aria-hidden="true">
                  {rescueInitials(rescueName)}
                </div>
                <div className={styles.rescueText}>
                  {rescueName} adopters get {rescueOffer.offer}.
                </div>
              </div>
            ) : (
              <div className={styles.proof}>
                {RESCUE_PROOF_COUNT} use one on their own {speciesPlural(species)}.
              </div>
            )}
          </div>
        </div>

        <div className={styles.productPanel}>
          <img
            className={styles.productImage}
            alt="A Fi Mini held in front of the Fi app, showing a dog's location and daily activity"
            src={miniInHandImage}
          />
        </div>

        {/* no price line here: checkout, one tap on, opens with what the tracker does and then the
            itemised total, so the price is stated once, where the shopper can act on it */}
        <div ref={endOfContentRef} className={styles.endOfContent} aria-hidden="true" />

        <div className={styles.ctaContainer}>
          <a
            className={styles.ctaPrimary}
            href={shopUrl}
            target={ctaOpensNewTab ? '_blank' : undefined}
            rel={ctaOpensNewTab ? 'noopener noreferrer' : undefined}
            onClick={onCtaClick}
          >
            Start tracking {petName}
          </a>
          {/* still goes to the user's chips; the label is the prototype's, which frames it as
              declining the offer rather than as a destination */}
          <a
            className={classNames(styles.ctaGhost, { [styles.ctaGhostHidden]: !hasReachedEnd })}
            href={Links.manageHome}
            onClick={onSecondaryClick}
          >
            Not now
          </a>
        </div>
      </div>

      <div className={styles.desktopSidebar}>
        <img
          className={styles.productImage}
          alt="A Fi Mini held in front of the Fi app, showing a dog's location and daily activity"
          src={miniInHandImage}
        />
      </div>
    </div>
  );
}
